import React, { useState } from "react";
import {
  ShoppingBag,
  ArrowRight,
  Plus,
  Minus,
  Trash2,
  ShieldCheck,
  ChevronLeft,
} from "lucide-react";
import {
  PACKAGES,
  extraChildPrice,
  lineBreakdown,
  cartPrice,
  linePrice,
  money,
} from "../shared/catalog.js";
import { Button, Field, Modal, PackageArt } from "./components.jsx";

const emptyChild = () => ({ name: "", age: "", note: "" });

export function Customize({ value, onClose, onAdd, disabled }) {
  const pack = PACKAGES.find((p) => p.id === value.packageId);
  const [children, setChildren] = useState(
    value.item?.children || [emptyChild()],
  );
  function childField(index, key, value) {
    setChildren(
      children.map((c, i) => (i === index ? { ...c, [key]: value } : c)),
    );
  }
  return (
    <Modal title="Make it your family’s magic" onClose={onClose} wide>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onAdd({
            packageId: pack.id,
            children: children.map((c) => ({
              ...c,
              name: c.name.trim(),
              age: Number(c.age),
            })),
          });
        }}
      >
        <div className="customize-intro">
          <PackageArt index={PACKAGES.indexOf(pack)} small />
          <div>
            <span className="eyebrow">
              {pack.kind === "letter" ? "YOUR SANTA LETTERS" : "YOUR FAMILY PACKAGE"}
            </span>
            <h3>{pack.name}</h3>
            <p>
              {pack.kind === "letter"
                ? `Each child gets their own reply from Santa, ${money(pack.price)} per letter.`
                : `Includes up to ${pack.includedChildren} children. Each extra child adds ${money(extraChildPrice(pack))} for a letter and physical items.`}
            </p>
          </div>
        </div>
        <div className="child-count">
          <span>How many little believers?</span>
          <div className="stepper">
            <button
              type="button"
              aria-label="One fewer child"
              disabled={children.length <= 1}
              onClick={() => setChildren(children.slice(0, -1))}
            >
              <Minus size={15} />
            </button>
            <strong>{children.length}</strong>
            <button
              type="button"
              aria-label="One more child"
              disabled={children.length >= 20}
              onClick={() => setChildren([...children, emptyChild()])}
            >
              <Plus size={15} />
            </button>
          </div>
        </div>
        <div className="children-fields">
          {children.map((child, i) => (
            <fieldset key={i}>
              <legend>
                <span>✧</span> Child {i + 1}
                {pack.kind !== "letter" && i >= pack.includedChildren && (
                  <small>+{money(extraChildPrice(pack))}</small>
                )}
              </legend>
              <div className="form-row">
                <Field label="First name">
                  <input
                    required
                    maxLength={60}
                    value={child.name}
                    onChange={(e) => childField(i, "name", e.target.value)}
                    placeholder="Their first name"
                    autoComplete="off"
                  />
                </Field>
                <Field label="Age">
                  <input
                    required
                    type="number"
                    min="0"
                    max="21"
                    value={child.age}
                    onChange={(e) => childField(i, "age", e.target.value)}
                    placeholder="Age"
                  />
                </Field>
              </div>
              <Field label="A little note for Santa (optional)">
                <textarea
                  maxLength={600}
                  rows={2}
                  value={child.note}
                  onChange={(e) => childField(i, "note", e.target.value)}
                  placeholder="A favorite thing, proud moment, or something Santa should know…"
                />
              </Field>
            </fieldset>
          ))}
        </div>
        <div className="modal-total">
          <div>
            <small>Your package total</small>
            <strong>
              {money(linePrice({ packageId: pack.id, children }))}
            </strong>
            <small>{lineBreakdown({ packageId: pack.id, children })}</small>
          </div>
          <Button type="submit" disabled={disabled}>
            {value.index === null ? "Add to cart" : "Save changes"}
            <ShoppingBag size={17} />
          </Button>
        </div>
      </form>
    </Modal>
  );
}

export function Cart({
  cart,
  config,
  onClose,
  onEdit,
  onRemove,
  onCheckout,
  onShop,
}) {
  return (
    <Modal title="Your little Christmas cart" onClose={onClose}>
      <div className="cart-body">
        {cart.length ? (
          <>
            <p className="muted">A little together-time is on its way.</p>
            <div className="cart-items">
              {cart.map((item, i) => {
                const pack = PACKAGES.find((p) => p.id === item.packageId);
                return (
                  <article className="cart-item" key={i}>
                    <PackageArt index={PACKAGES.indexOf(pack)} small />
                    <div>
                      <h3>{pack.name}</h3>
                      <p>{item.children.map((c) => c.name).join(", ")}</p>
                      <small>
                        {item.children.length}{" "}
                        {item.children.length === 1 ? "child" : "children"} ·
                        Delivery included
                      </small>
                      <div className="cart-item-actions">
                        <button onClick={() => onEdit(i)}>Edit details</button>
                        <button
                          onClick={() => onRemove(i)}
                          aria-label={`Remove ${pack.name}`}
                        >
                          <Trash2 size={13} /> Remove
                        </button>
                      </div>
                    </div>
                    <strong>{money(linePrice(item))}</strong>
                  </article>
                );
              })}
            </div>
            <div className="cart-totals">
              <span>
                Delivery <strong>Our treat</strong>
              </span>
              <span className="cart-grand-total">
                Your total <strong>{money(cartPrice(cart))}</strong>
              </span>
            </div>
            <Button
              className="full-width"
              onClick={onCheckout}
              disabled={!config?.acceptingOrders}
            >
              Continue to checkout <ArrowRight size={17} />
            </Button>
            <button className="text-link cart-continue" onClick={onShop}>
              Add another package
            </button>
            <p className="fine-print">
              <ShieldCheck size={14} /> Choose how to pay at checkout.
            </p>
          </>
        ) : (
          <div className="empty-state">
            <ShoppingBag size={40} strokeWidth={1} />
            <h3>A little room for magic.</h3>
            <p>Your cart is waiting for a Christmas surprise.</p>
            <Button onClick={onShop}>
              Explore the packages <ArrowRight size={16} />
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
}

export function OrderPage({ config, cart, choose, navigate, openCart }) {
  return (
    <section className="section order-page">
      <button className="text-link" onClick={() => navigate("/")}>
        <ChevronLeft size={16} /> Back to the Christmas story
      </button>
      <div className="section-intro">
        <span className="eyebrow">LET’S MAKE A LITTLE MAGIC</span>
        <h1>Choose your family’s surprise.</h1>
        <p>
          Pick a package, tell us about the kids, and we’ll take it from there.
        </p>
      </div>
      <div className="order-options">
        {PACKAGES.map((p, i) => (
          <button
            className="order-option"
            key={p.id}
            onClick={() => choose(p.id)}
            disabled={!config?.acceptingOrders}
          >
            <PackageArt index={i} />
            <div>
              <span className="eyebrow">{p.tag}</span>
              <h2>{p.name}</h2>
              <p>{p.description}</p>
              <strong>
                {money(p.price)}{" "}
                <small>
                  {p.kind === "letter"
                    ? "per child"
                    : `includes ${p.includedChildren} children`}
                </small>
              </strong>
              <span className="text-link">
                Personalize & add to cart <ArrowRight size={16} />
              </span>
            </div>
          </button>
        ))}
      </div>
      {cart.length > 0 && (
        <div className="cart-summary-bar">
          <span>
            <ShoppingBag size={20} /> {cart.length}{" "}
            {cart.length === 1 ? "package" : "packages"} in your cart{" "}
            <strong>{money(cartPrice(cart))}</strong>
          </span>
          <Button onClick={openCart}>
            Review your cart <ArrowRight size={16} />
          </Button>
        </div>
      )}
    </section>
  );
}
